import { attributesHelper, createArticleComparer } from "@bundles/fast-calculator/data/helpers"
import { CalcGenArt } from "@bundles/fast-calculator/data/model"
import { Box, Icon, IconButton, styled } from "@tm/components"
import { FastCalculator } from "@tm/data"
import { Article } from "@tm/models"
import { useEffect, useRef, useState } from "react"
import CalcInputComponent from "./calcInput"

type OeGroupsProps = {
    item: FastCalculator.CalcArticle
    genArt: CalcGenArt
    articles: Article[]
}

export const OeGroups = ({ item, genArt, articles }: OeGroupsProps) => {
    const [showNextArrow, setShowNextArrow] = useState(false)
    const [showPrevArrow, setShowPrevArrow] = useState(false)
    const scrollRef = useRef<HTMLDivElement>(null)

    const checkArrows = () => {
        if (scrollRef.current) {
            const { scrollLeft, scrollWidth, clientWidth } = scrollRef.current
            setShowPrevArrow(scrollLeft > 0)
            setShowNextArrow(scrollLeft + clientWidth < scrollWidth - 5)
        }
    }

    useEffect(() => {
        const container = scrollRef.current
        if (!container) {
            return
        }

        checkArrows()
        window.addEventListener("resize", checkArrows)

        return () => {
            window.removeEventListener("resize", checkArrows)
        }
    }, [])

    function handUpdateArrowVisibilityAfterScroll(container: HTMLDivElement, delay = 600) {
        setTimeout(() => {
            const { scrollLeft, scrollWidth, clientWidth } = container
            const atStart = scrollLeft <= 0
            const atEnd = scrollLeft + clientWidth >= scrollWidth - 1

            setShowPrevArrow((prev) => (prev === !atStart ? prev : !atStart))
            setShowNextArrow((prev) => (prev === !atEnd ? prev : !atEnd))
        }, delay)
    }

    const handleScrollToNext = () => {
        const container = scrollRef.current
        if (!container) {
            return
        }

        const children = Array.from(container.children) as HTMLElement[]
        const scrollRightEdge = container.scrollLeft + container.clientWidth

        const next = children.find((child) => child.offsetLeft + child.offsetWidth > scrollRightEdge + 1)

        if (next) {
            container.scrollTo({
                left: next.offsetLeft,
                behavior: "smooth",
            })

            handUpdateArrowVisibilityAfterScroll(container)
        }
    }

    const handleScrollToPrev = () => {
        const container = scrollRef.current
        if (!container) {
            return
        }

        const children = Array.from(container.children) as HTMLElement[]
        const { scrollLeft } = container

        const prev = [...children].reverse().find((child) => child.offsetLeft + child.offsetWidth < scrollLeft - 1)

        container.scrollTo({
            left: prev?.offsetLeft ?? 0,
            behavior: "smooth",
        })

        handUpdateArrowVisibilityAfterScroll(container)
    }

    if (!item.oeGroups?.length || (item.oeGroups.length === 1 && item.oeGroups[0].label === "...")) {
        return null
    }

    return (
        <StyledOeGroupsBox className="article-oegroups">
            {showPrevArrow && (
                <IconButton sx={{ marginBottom: "0.5em", minWidth: "1.5em" }} onClick={handleScrollToPrev}>
                    <Icon name="arrow-left" />
                </IconButton>
            )}
            <StyledBox ref={scrollRef}>
                {item.oeGroups.map((oeGroup, index) => {
                    let article: Article | undefined
                    let allAttrs = 0

                    if (oeGroup.isDisabled) {
                        article = articles?.find(createArticleComparer(item, genArt))
                    } else if (
                        oeGroup.value2 &&
                        oeGroup.value2Type === FastCalculator.ECalcInputValue.Article &&
                        (oeGroup.value2.type === FastCalculator.ECalcArticle.Article ||
                            oeGroup.value2.type === FastCalculator.ECalcArticle.AlternativeArticle)
                    ) {
                        article = articles?.find(createArticleComparer(oeGroup.value2, genArt))
                    }

                    if (article) {
                        if (oeGroup.value2) {
                            allAttrs = attributesHelper(
                                false,
                                article,
                                [...(oeGroup.value2.attributes || []), ...(oeGroup.value2.vknAttributes || [])].filter((x) => x.existsInSlfa)
                            ) as number
                        } else {
                            allAttrs = attributesHelper(
                                false,
                                article,
                                [...(item.attributes || []), ...(item.vknAttributes || [])].filter((x) => x.existsInSlfa)
                            ) as number
                        }
                    }

                    return (
                        <StyledOeGroupBox key={index}>
                            <CalcInputComponent item={oeGroup} selected={oeGroup.isDisabled} hasHits={!!allAttrs} />
                        </StyledOeGroupBox>
                    )
                })}
            </StyledBox>
            {showNextArrow && (
                <IconButton sx={{ marginBottom: "0.5em", minWidth: "1.5em" }} onClick={handleScrollToNext}>
                    <Icon name="arrow-right" />
                </IconButton>
            )}
        </StyledOeGroupsBox>
    )
}

const StyledOeGroupsBox = styled(Box)({
    display: "flex",
    marginBottom: "0.25em",
    overflowX: "hidden",
    flexWrap: "nowrap",
    scrollSnapType: "x mandatory",
    scrollPaddingLeft: "0.5em",
})

const StyledBox = styled(Box)({
    display: "flex",
    overflowX: "hidden",
    scrollSnapType: "x mandatory",
    scrollPaddingLeft: "0.5em",
    paddingBottom: "4px",
})

const StyledOeGroupBox = styled(Box)({
    position: "relative",
    marginBottom: "0.25em",
    marginRight: "0.5em",
    flex: "0 0 auto",
    scrollSnapAlign: "start",
})
